/**
 * Local history of sends/receives. Guest-first: no account, no backend
 * — it is persisted in `localStorage`. With an account (future) it would sync encrypted.
 */

import { isRecord } from "@enviar/core";

const STORAGE_KEY = "enviar.history.v1";
const MAX_ENTRIES = 100;

export interface HistoryEntry {
  id: string;
  direction: "sent" | "received";
  at: number;
  fileNames: string[];
  totalBytes: number;
}

function isEntry(value: unknown): value is HistoryEntry {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    (value.direction === "sent" || value.direction === "received") &&
    typeof value.at === "number" &&
    Array.isArray(value.fileNames) &&
    value.fileNames.every((n) => typeof n === "string") &&
    typeof value.totalBytes === "number"
  );
}

export function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
  } catch {
    return [];
  }
}

export function addHistoryEntry(entry: Omit<HistoryEntry, "id" | "at">): HistoryEntry {
  const full: HistoryEntry = {
    ...entry,
    id: crypto.randomUUID(),
    at: Date.now(),
  };
  const next = [full, ...loadHistory()].slice(0, MAX_ENTRIES);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage full/blocked: history is best-effort */
  }
  return full;
}

export function clearHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
